/**
 * AdminThemeProvider — 프로젝트 테마(주조색 등)를 한 곳에서 주입 (우선순위: 프로젝트별 컬러)
 *
 * primary 한 값만 바꾸면 이 라이브러리의 모든 컴포넌트(버튼·체크박스·툴팁·배지·
 * 페이지네이션 활성·텍스트 선택색 등)의 포인트 색이 함께 바뀐다.
 * 내부적으로 CSS 변수(--primary 등)를 :root 에 주입하므로, portal 로 뜨는
 * 모달/드로어/툴팁까지 동일하게 적용된다.
 *
 * 상호작용이 없어 서버 컴포넌트로 동작하며 SSR 시에도 깜빡임이 없다.
 * 보통 루트 레이아웃(app/layout.tsx)이나 (admin) 레이아웃 최상단에서 감싼다.
 *
 * @example
 * // app/layout.tsx 또는 app/(admin)/layout.tsx
 * <AdminThemeProvider primary="#4f46e5">
 *   {children}
 * </AdminThemeProvider>
 *
 * @example
 * // 색·대비·포커스링까지 지정
 * <AdminThemeProvider primary="oklch(0.55 0.2 264)" primaryForeground="#fff" ring="#4f46e5">
 *   {children}
 * </AdminThemeProvider>
 */
import type { ReactNode } from "react";

export interface AdminThemeProviderProps {
  /** 주조색. 버튼/체크박스/배지/툴팁 등 포인트 색 (CSS 색상 문자열) */
  primary?: string;
  /** 주조색 위 글자색 (기본은 흰색 계열 유지) */
  primaryForeground?: string;
  /** 포커스 링 색. 주조색과 맞추고 싶을 때 지정 */
  ring?: string;
  /** 추가로 덮어쓸 임의의 CSS 변수 (예: { "--radius": "0.5rem" }) */
  vars?: Record<string, string>;
  children: ReactNode;
}

export function AdminThemeProvider({
  primary,
  primaryForeground,
  ring,
  vars,
  children,
}: AdminThemeProviderProps) {
  const decls = [
    primary && `--primary:${primary};`,
    primaryForeground && `--primary-foreground:${primaryForeground};`,
    ring && `--ring:${ring};`,
    ...Object.entries(vars ?? {}).map(([k, v]) => `${k}:${v};`),
  ]
    .filter(Boolean)
    .join("");

  return (
    <>
      {decls && <style>{`:root{${decls}}`}</style>}
      {children}
    </>
  );
}
